import { ICategoryDtoCategoryTypeEnum } from '@/dtos/models/ICategoryDto';
import { IHealthPlanTypePropertyDtoHealthPlanTypesEnum } from '@/dtos/models/IHealthPlanTypePropertyDto';

export interface CustomCoverageLevelPayload {
  coverageName: string;
  coverageCode: string;
}

export interface CategorySpendLimitPayload {
  id?: string;
  planId?: string;
  categoryType: string;
  categorySpendLimit: string;
}

export interface AccountFundingConfigPaylod {
  id?: string;
  planId: string;
  planName?: string;
  maxElectionAmount?: number;
  minElectionAmount?: number;
  autoEnrollmentAmount?: string;
  perPersonSpendLimit?: string;
  canSetHealthPlanSpendLimits?: boolean;
}

export interface CoverageLevelPayload {
  id?: string;
  name?: string;
  coverageId: string;
  parentId?: string;
  title: string;
  coverageCode?: string;
  planDeductible?: string;
  memberDeductible?: string;
  embeddedAmount?: string;
  isDeletable?: boolean;
  accountFundingConfigs?: AccountFundingConfigPaylod[];
  categorySpendLimits?: CategorySpendLimitPayload[];
  isHighlighted?: boolean;
  customTriggerAmounts?: Record<string, string>;
}

export const CUSTOM_TRIGGER_AMOUNT_PREFIX = 'customTrigger_';

export const AUTO_FUNDING_PREFIX = 'autoFundingAmount_';
export const SPEND_LIMIT_PREFIX = 'perPersonSpendLimit_';
export const CATEGORY_SPEND_LIMIT_PREFIX = 'categorySpendLimit_';

export const toAutoFundingKey = (planId: string) => `${AUTO_FUNDING_PREFIX}${planId}`;
export const toSpendLimitKey = (planId: string) => `${SPEND_LIMIT_PREFIX}${planId}`;
export const toCategorySpendLimitKey = (planId: string, categoryType: string) =>
  `${CATEGORY_SPEND_LIMIT_PREFIX}${planId}_${categoryType}`;

/** Maps health plan type keys to their spend limit category types and labels */
export const HEALTH_PLAN_TYPE_TO_CATEGORY: Record<string, { categoryType: string; label: string }> = {
  [IHealthPlanTypePropertyDtoHealthPlanTypesEnum.MEDICAL]: {
    categoryType: ICategoryDtoCategoryTypeEnum.MEDICAL,
    label: 'Medical spend limit',
  },
  [IHealthPlanTypePropertyDtoHealthPlanTypesEnum.DENTAL]: {
    categoryType: ICategoryDtoCategoryTypeEnum.DENTAL,
    label: 'Dental spend limit',
  },
  [IHealthPlanTypePropertyDtoHealthPlanTypesEnum.VISION]: {
    categoryType: ICategoryDtoCategoryTypeEnum.VISION,
    label: 'Vision spend limit',
  },
  [IHealthPlanTypePropertyDtoHealthPlanTypesEnum.RX]: {
    categoryType: ICategoryDtoCategoryTypeEnum.PHARMACY,
    label: 'Pharmacy spend limit',
  },
};

/** Fixed display order for category spend limit fields (matches dropdown order) */
export const CATEGORY_SPEND_LIMIT_ORDER: string[] = [
  IHealthPlanTypePropertyDtoHealthPlanTypesEnum.MEDICAL,
  IHealthPlanTypePropertyDtoHealthPlanTypesEnum.DENTAL,
  IHealthPlanTypePropertyDtoHealthPlanTypesEnum.VISION,
  IHealthPlanTypePropertyDtoHealthPlanTypesEnum.RX,
];

/** UI labels & messages */
export const AUTO_FUNDING_LABEL = 'Auto funding';
export const HINT_RIGHT_ALIGN_KEY = 'right';

/** Deductible field name constants */
export const COVERAGE_CODE_FIELD = 'coverageCode';
export const PLAN_DEDUCTIBLE_FIELD = 'planDeductible';
export const MEMBER_DEDUCTIBLE_FIELD = 'memberDeductible';
export const EMBEDDED_AMOUNT_FIELD = 'embeddedAmount';

export const PLAN_DEDUCTIBLE_TRIGGER_ID = 1;
export const MEMBER_DEDUCTIBLE_TRIGGER_ID = 2;
export const EMBEDDED_DEDUCTIBLE_TRIGGER_ID = 3;

export const DEDUCTIBLE_FIELDS = [
  {
    name: PLAN_DEDUCTIBLE_FIELD,
    label: 'Plan deductible',
    triggerId: PLAN_DEDUCTIBLE_TRIGGER_ID,
  },
  {
    name: MEMBER_DEDUCTIBLE_FIELD,
    label: 'Member deductible',
    triggerId: MEMBER_DEDUCTIBLE_TRIGGER_ID,
  },
  {
    name: EMBEDDED_AMOUNT_FIELD,
    label: 'Embedded amount',
    triggerId: EMBEDDED_DEDUCTIBLE_TRIGGER_ID,
  },
] as const;
